// The advanced search form and the search box share one query language (search-query operators
// such as from: and has:attachment). These turn the form's fields into a query and back.

export type SearchFields = {
  from: string;
  to: string;
  subject: string;
  words: string;
  without: string;
  folder: string;
  hasAttachment: boolean;
  unread: boolean;
  flagged: boolean;
  after: string;
  before: string;
  larger: string;
};

export const EMPTY_FIELDS: SearchFields = {
  from: "",
  to: "",
  subject: "",
  words: "",
  without: "",
  folder: "",
  hasAttachment: false,
  unread: false,
  flagged: false,
  after: "",
  before: "",
  larger: "",
};

const quote = (value: string) => (/[\s"()]/.test(value) ? `"${value.replace(/"/g, "")}"` : value);

/** Words split on spaces, keeping "quoted phrases" together. */
const tokens = (text: string) => text.match(/-?"[^"]*"|\S+/g) ?? [];

export const buildQuery = (fields: SearchFields): string =>
  [
    fields.from.trim() && `from:${quote(fields.from.trim())}`,
    fields.to.trim() && `to:${quote(fields.to.trim())}`,
    fields.subject.trim() && `subject:${quote(fields.subject.trim())}`,
    fields.words.trim(),
    ...tokens(fields.without).map((word) => `-${word.replace(/^-/, "")}`),
    fields.folder && `in:${quote(fields.folder)}`,
    fields.hasAttachment && "has:attachment",
    fields.unread && "is:unread",
    fields.flagged && "is:flagged",
    fields.after && `after:${fields.after}`,
    fields.before && `before:${fields.before}`,
    fields.larger.trim() && `larger:${fields.larger.trim().replace(/\s+/g, "")}`,
  ]
    .filter(Boolean)
    .join(" ");

const unquote = (value: string) => value.replace(/^"(.*)"$/, "$1");

/** Reads a query back into form fields; anything the form can't show stays in "words". */
export const parseQuery = (query: string): SearchFields => {
  const fields = { ...EMPTY_FIELDS };
  const words: string[] = [];
  const without: string[] = [];
  for (const token of query.match(/-?[a-z]+:"[^"]*"|-?"[^"]*"|\S+/gi) ?? []) {
    const match = /^([a-z]+):(.+)$/i.exec(token);
    const key = match?.[1]?.toLowerCase();
    const value = unquote(match?.[2] ?? "");
    if (key === "from" && !fields.from) fields.from = value;
    else if (key === "to" && !fields.to) fields.to = value;
    else if (key === "subject" && !fields.subject) fields.subject = value;
    else if (key === "in" && !fields.folder) fields.folder = value;
    else if (key === "has" && value === "attachment") fields.hasAttachment = true;
    else if (key === "is" && value === "unread") fields.unread = true;
    else if (key === "is" && value === "flagged") fields.flagged = true;
    else if (key === "after" && /^\d{4}-\d{2}-\d{2}$/.test(value)) fields.after = value;
    else if (key === "before" && /^\d{4}-\d{2}-\d{2}$/.test(value)) fields.before = value;
    else if (key === "larger" && !fields.larger) fields.larger = value;
    else if (/^-[^-]/.test(token) && !token.includes(":")) without.push(token.slice(1));
    else words.push(token);
  }
  fields.words = words.join(" ");
  fields.without = without.join(" ");
  return fields;
};

const OPERATOR = /(^|\s)-?[a-z]+:\S/i;

/** A few words with no operators, quotes, negation or OR reads as a description. */
export const looksNatural = (text: string) => {
  const words = text.trim().split(/\s+/);
  return (
    words.length >= 3 &&
    !OPERATOR.test(text) &&
    !text.includes('"') &&
    !words.some((word) => word.startsWith("-") || word === "OR")
  );
};

/** A search split into its parts: operators with their values, phrases and single words. */
export const queryParts = (query: string): string[] =>
  query.match(/-?[a-z]+:"[^"]*"|-?"[^"]*"|\S+/gi) ?? [];

const NAMES: Record<string, string> = {
  from: "From",
  to: "To",
  cc: "Cc",
  subject: "Subject",
  body: "Body",
  filename: "File",
  in: "In",
  label: "Label",
  before: "Before",
  after: "After",
  larger: "Larger than",
  smaller: "Smaller than",
};

const FLAGS: Record<string, string> = {
  "has:attachment": "Has attachment",
  "is:unread": "Unread",
  "is:read": "Read",
  "is:flagged": "Flagged",
  "is:answered": "Answered",
};

/** How a part reads on its chip. */
export const partLabel = (part: string) => {
  const negate = part.startsWith("-") && part.length > 1;
  const bare = negate ? part.slice(1) : part;
  const flag = FLAGS[bare.toLowerCase()];
  const match = /^([a-z]+):(.+)$/i.exec(bare);
  const name = match ? NAMES[match[1]?.toLowerCase() ?? ""] : undefined;
  const text =
    flag ??
    (name && match
      ? `${name}: ${(match[2] ?? "").replace(/^"(.*)"$/, "$1")}`
      : bare.replace(/^"(.*)"$/, "$1"));
  return negate ? `Not ${text}` : text;
};
